import type { StaffRole } from "@lafayette/auth";

import { roleLabels } from "../team/labels";
import { Avatar } from "../ui/avatar";
import { Button } from "../ui/button";
import { cn } from "../ui/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Icon } from "../ui/icon";

// The card at the bottom of the sidebar: who is signed in (avatar, name, role, a presence dot) and a menu with what used to stand loose
// under the menu: the collapse button, Sign out and the facts of the environment. The drawer of a narrow screen has no collapse entry.

export type EnvironmentFacts = Readonly<{
  environment: string;
  version: string;
  buildId: string;
  gitSha: string;
}>;

export type StaffPerson = Readonly<{ id: string; name: string }>;

type UserCardProps = Readonly<{
  person: StaffPerson;
  roles: readonly StaffRole[];
  /** Icons only: the card is the avatar, and the name and the role stay for a screen reader. */
  collapsed: boolean;
  environment: EnvironmentFacts;
  isSigningOut: boolean;
  onSignOut: () => void;
  /** The collapse entry; absent in the drawer. */
  onToggleCollapsed?: (() => void) | undefined;
}>;

/** `Supervisor`, or `Concierge, Supervisor` for a person who holds more than one role. */
function roleText(roles: readonly StaffRole[]): string {
  return roles.map((role) => roleLabels[role]).join(", ");
}

export function UserCard({
  person,
  roles,
  collapsed,
  environment,
  isSigningOut,
  onSignOut,
  onToggleCollapsed,
}: UserCardProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "h-auto w-full min-w-0 justify-start gap-2.5 rounded-2xl border border-hairline bg-card p-2.5 text-left font-normal text-card-foreground compact:h-auto compact:p-1.5",
            collapsed && "justify-center border-transparent bg-transparent p-0",
          )}
        >
          <span className="relative shrink-0">
            <Avatar id={person.id} name={person.name} />
            {/* The dot says the session is signed in (the console has no live presence yet); the words are for a screen reader. */}
            <span
              aria-hidden="true"
              className="absolute right-0 bottom-0 size-3 rounded-full border-2 border-card bg-success"
            />
          </span>
          <span className={cn("grid min-w-0 flex-1 leading-tight", collapsed && "sr-only")}>
            <span className="truncate font-serif text-[17px] font-medium">{person.name}</span>
            <span className="truncate text-[13px] text-muted-foreground">
              {roleText(roles)}
              <span className="sr-only">, signed in</span>
            </span>
          </span>
          <Icon
            name="unfold-more-round"
            size="md"
            className={cn("text-muted-foreground", collapsed && "hidden")}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-60">
        {onToggleCollapsed ? (
          <DropdownMenuItem onSelect={onToggleCollapsed}>
            <Icon name="sidebar-left" size="md" />
            {collapsed ? "Expand sidebar" : "Collapse sidebar"}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem disabled={isSigningOut} onSelect={onSignOut}>
          <Icon name="logout-01" size="md" />
          {isSigningOut ? "Signing out…" : "Sign out"}
        </DropdownMenuItem>
        <DropdownMenuLabel className="mt-1 grid gap-0.5 border-t border-hairline pt-2 font-normal">
          <span className="environment-card grid gap-0.5">
            <span className="font-semibold text-foreground">{environment.environment}</span>
            <span>{environment.version}</span>
            <span>build {environment.buildId}</span>
            <span>sha {environment.gitSha.slice(0, 12)}</span>
          </span>
        </DropdownMenuLabel>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
